import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Gavel, CheckCircle2, XCircle, Clock } from "lucide-react";
import api, { formatApiError, API } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

export function DisputePhoto({ path, testId = "dispute-photo-view" }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false; let created = "";
    (async () => {
      try {
        const res = await fetch(`${API}/files/${path}`, { credentials: "include" });
        if (!res.ok) throw new Error("fetch failed");
        created = URL.createObjectURL(await res.blob());
        if (!cancelled) setBlobUrl(created);
      } catch (_) { if (!cancelled) setErr(true); }
    })();
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [path]);
  if (err) return <div className="mt-2 text-xs text-red-700">Foto non disponibile</div>;
  if (!blobUrl) return <div className="mt-2 text-xs text-slate-500">Caricamento foto...</div>;
  return (
    <a href={blobUrl} target="_blank" rel="noreferrer" className="mt-3 block" data-testid={testId}>
      <img src={blobUrl} alt="Prova" className="max-h-48 rounded-lg border border-slate-200" />
    </a>
  );
}

const deadlinePassed = (dl) => !!dl && new Date(dl).getTime() < Date.now();

function DefenseDeadline({ deadline, isWinner, resolved }) {
  if (!deadline || resolved) return null;
  const passed = deadlinePassed(deadline);
  const hoursLeft = Math.max(0, Math.ceil((new Date(deadline).getTime() - Date.now()) / 36e5));
  return passed ? (
    <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-amber-300 px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest" data-testid="defense-deadline-expired">
      <Clock className="w-3 h-3" /> Termine difesa scaduto · nessuna risposta del fornitore
    </div>
  ) : (
    <div className="mt-3 text-xs text-slate-700 flex items-center gap-1.5" data-testid="defense-deadline-pending">
      <Clock className="w-3.5 h-3.5 text-amber-600" />
      {isWinner ? <>Hai <b>{hoursLeft}h</b> per inviare la tua difesa (entro {new Date(deadline).toLocaleString("it-IT")})</> : <>In attesa della difesa del fornitore · scade {new Date(deadline).toLocaleString("it-IT")} ({hoursLeft}h)</>}
    </div>
  );
}

function SupplierDefenseForm({ orderId, onDone }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (text.trim().length < 10) return toast.error("Scrivi almeno 10 caratteri");
    setBusy(true);
    try {
      let photo_path = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads/dispute-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_path = data.path;
      }
      await api.post(`/orders/${orderId}/dispute/respond`, { response: text.trim(), photo_path });
      toast.success("Difesa inviata all'amministratore");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="dispute-defense-form">
      <Label htmlFor="defense">La tua difesa <span className="text-slate-400 text-xs">(min. 10 caratteri)</span></Label>
      <Textarea id="defense" rows={3} maxLength={800} value={text} onChange={e => setText(e.target.value)} placeholder="Spiega la tua versione: merce consegnata, orario, prove..." data-testid="dispute-defense-input" />
      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={e => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-slate-700 file:mr-3 file:rounded-full file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold" data-testid="dispute-defense-photo-input" />
      <Button onClick={send} disabled={busy} className="ga-btn-primary rounded-full w-full" data-testid="dispute-defense-submit">
        <ShieldAlert className="w-4 h-4 mr-2" />{busy ? "Invio..." : "Invia difesa"}
      </Button>
    </div>
  );
}

function AdminResolve({ orderId, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const resolve = async (outcome) => {
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/dispute/resolve`, { outcome, note });
      toast.success(outcome === "accepted" ? "Contestazione accolta · pagamento annullato/rimborsato" : "Contestazione respinta · payout rilasciato");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-xl border border-slate-900 bg-white p-3 space-y-2" data-testid="dispute-admin-panel">
      <div className="text-xs font-mono uppercase tracking-widest text-slate-900 flex items-center gap-1.5"><Gavel className="w-3.5 h-3.5" /> Decisione amministratore</div>
      <Textarea rows={2} maxLength={800} value={note} onChange={e => setNote(e.target.value)} placeholder="Nota (opzionale)" data-testid="dispute-resolve-note" />
      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => resolve("accepted")} disabled={busy} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="dispute-accept-button"><CheckCircle2 className="w-4 h-4 mr-1" />Accogli · rimborsa acquirente</Button>
        <Button onClick={() => resolve("rejected")} disabled={busy} variant="outline" className="rounded-full" data-testid="dispute-reject-button"><XCircle className="w-4 h-4 mr-1" />Respingi · paga fornitore</Button>
      </div>
    </div>
  );
}

export default function DisputePanel({ order, isWinner, isAdmin, onReload }) {
  const d = order.dispute;
  if (!d || !["disputed", "refunded"].includes(order.confirmation_status) && !d.resolution) return null;
  const res = d.resolution;
  const acceptedLabel = d.type === "mancata_consegna"
    ? "Accolta · ordine annullato, pagamento annullato / rimborsato all'acquirente"
    : "Accolta · pagamento annullato / rimborsato all'acquirente";
  return (
    <div className={`ga-card p-4 border-2 ${res ? (res.outcome === "accepted" ? "border-red-300 bg-red-50/40" : "border-emerald-300 bg-emerald-50/40") : "border-red-300 bg-red-50/60"}`} data-testid="dispute-banner">
      <div className="text-xs font-mono uppercase tracking-widest text-red-700">{res ? "Contestazione chiusa" : "Contestazione aperta"}{d.type === "mancata_consegna" && " · mancata consegna"}</div>
      <div className="mt-1 text-sm text-slate-900 font-semibold">
        {res ? (res.outcome === "accepted" ? acceptedLabel : "Respinta · pagamento rilasciato al fornitore") : "Pagamento congelato in attesa di mediazione"}
      </div>
      <div className="text-xs text-slate-700 mt-1">Motivo acquirente: "{d.reason}"</div>
      {d.photo_path && <DisputePhoto path={d.photo_path} />}
      {d.supplier_response ? (
        <div className="mt-3 ml-3 border-l-2 border-slate-400 pl-3" data-testid="dispute-supplier-response">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-600">Difesa fornitore · {new Date(d.supplier_response.sent_at).toLocaleString("it-IT")}</div>
          <div className="text-xs text-slate-800 mt-0.5">"{d.supplier_response.text}"</div>
          {d.supplier_response.photo_path && <DisputePhoto path={d.supplier_response.photo_path} testId="dispute-defense-photo-view" />}
        </div>
      ) : (
        <>
          <DefenseDeadline deadline={d.defense_deadline} isWinner={isWinner} resolved={!!res} />
          {!res && isWinner && !deadlinePassed(d.defense_deadline) && <SupplierDefenseForm orderId={order.id} onDone={onReload} />}
        </>
      )}
      {res && (
        <div className="mt-2 text-xs text-slate-600">
          Decisione del {new Date(res.resolved_at).toLocaleString("it-IT")}{res.note ? ` · "${res.note}"` : ""}
          {order.refund && <span className="block mt-0.5 font-mono">Rimborso: {order.refund.status}{order.refund.reference ? ` · ${order.refund.reference}` : ""}</span>}
        </div>
      )}
      {!res && isAdmin && <AdminResolve orderId={order.id} onDone={onReload} />}
    </div>
  );
}
